// Client-owned domain vocabulary. This file deliberately imports nothing.

export type DeviceType = "android" | "ios" | "web";

export type SubscriptionManagementProvider =
  | "stripe"
  | "googlePlay"
  | "appStore";

export type AgentConnectionSummary = {
  id: string;
  assistant?: string;
  authorizedAt?: string;
  lastUsedAt?: string;
};

export type ServerErrorCode =
  | "InvalidEnvelope"
  | "UnknownAction"
  | "Unauthorized"
  | "InvalidInput"
  | "NotFound"
  | "InvalidFilePath"
  | "FileNotFound"
  | "ExpectedMultipartFormData"
  | "AudioFileRequired"
  | "AudioFileTooLarge"
  | "AudioDurationTooLong"
  | "WebhookFailed"
  | "Internal"
  | "InvalidWebhookPayload"
  | "InvalidAccessToken"
  | "DeviceIdentityLinked"
  | "InvalidEmail"
  | "UserNotFound"
  | "InvalidOneTimeCode"
  | "OneTimeCodeExpired"
  | "TranscriptNotFound"
  | "UnsupportedFile"
  | "OriginalFileUrlIsRequired"
  | "UnableToGetUploadData"
  | "NotAllowedToTranscribeFullAudio"
  | "OnlyVoiceRecordingAndAudioOrVideoImportCanBeRetranscribed"
  | "TranscriptAlreadyHasFullTranscript"
  | "TranscriptNotCompleted"
  | "UserNotSubscribed"
  | "LiveRecordingUrlNotFound"
  | "NoAudioFileAvailable"
  | "AudioProcessingFailed"
  | "TranscriptionFailed"
  | "ImageTranscriptionFailed"
  | "PdfTranscriptionFailed"
  | "AlreadySubscribed"
  | "SubscriptionNotFound"
  | "PdfFailed"
  | "TranslationFailed"
  | "SummaryFailed"
  | "FlashcardFailed"
  | "QuizFailed"
  | "RateLimitExceeded"
  | "EmptyTranscription"
  | "RewriteFailed";

export type ClientError =
  | ServerErrorCode
  | "unauthorized"
  | "networkUnavailable"
  | "timeout"
  | "invalidResponse"
  | "uploadAuthorizationExpired"
  | "uploadRejected"
  | "cancelled";

export type ActionError =
  | ClientError
  | "needsPremium"
  | "purchaseCancelled"
  | "purchaseFailed"
  | "purchaseUnavailable";

export type Result<Value, ErrorCode extends string> =
  | { ok: true; value: Value }
  | { ok: false; error: ErrorCode };

export type ClientResult<Value> = Result<Value, ClientError>;
export type ActionResult<
  Value,
  ErrorCode extends ActionError = ActionError,
> = Result<Value, ErrorCode>;

export type SearchResultItem = {
  transcriptId: string;
  transcriptType: Transcript["type"];
  transcriptName: string;
  textStart: number;
  text: string;
};

export type RemoteConfig = {
  country: string;
  webPlans: {
    monthly: SubscriptionPlan;
    annual: SubscriptionPlan;
  };
};

export type SubscriptionOffer = {
  subscriptionPlan: SubscriptionPlan;
  tier: "low" | "mid" | "high";
  billingPeriod: "week" | "month" | "year";
  currency: string;
  unitAmount: number;
};

export type LiveRecordingChunk = {
  noteId: string;
  format?: {
    deviceType: User["deviceType"];
    sampleRate: number;
    channels: number;
    bitsPerSample: number;
  };
  chunk?: number[];
  createdAt: string;
};

export type LiveRecordingFormat = NonNullable<LiveRecordingChunk["format"]>;

export type LiveRecordingStatus = {
  fileUrl?: string;
  uploadProgress: number;
};

export type ConnectedClient =
  | "chromeExtension"
  | "ios"
  | "android"
  | "macDesktop"
  | "windowsDesktop";

export type DictationClientPlatform =
  | "web"
  | "ios"
  | "android"
  | "desktop"
  | "chrome_extension";

export type DictationTrigger =
  | "floating_bubble"
  | "hotkey"
  | "keyboard"
  | "toolbar"
  | "practice"
  | "retry";

export type DictationDelivery = "direct_insert" | "review_sheet" | "clipboard";

export const PARAGRAPH_SENTENCE_LIMITS = [3, 5, 7] as const;
export type ParagraphSentenceLimit = (typeof PARAGRAPH_SENTENCE_LIMITS)[number];

export type User = {
  id: string;
  email: string;
  emailVerified: boolean;
  ip: string;
  country: string;
  theme?: "light" | "dark" | "system";
  oauthProvider: "google" | "apple" | "email" | "mobile";
  deviceLanguage: string;
  totalUsageCount?: number;
  totalUsageInSeconds?: number;
  dailyUsageCount?: { [date: string]: number };
  dailyUsageInSeconds?: { [date: string]: number };
  globalDictationUsageCount?: number;
  connectedClients?: ConnectedClient[];
  stripeCustomerId?: string;
  acquisitionAttribution?: Record<string, string>;
  acquisitionAttributionCapturedAt?: string;
  oneTimeCode?: number;
  deviceType: DeviceType;
  migrated?: boolean;
  unverifiedEmail?: string;
  deviceTimezone: string;
  createdAt: string;
  updatedAt: string;
  aiConsent?: { version: number; acceptedAt: string } | null;
  preferences?: {
    transcriptionLanguage?: string;
    // Missing means the user has not selected a dictation language; the
    // server uses OpenRouter Whisper V3 Nitro with automatic detection.
    dictationLanguage?: string;
    // Most-recent-first explicit choices shared by every dictation client.
    recentDictationLanguages?: string[];
    paragraphSentenceLimit?: ParagraphSentenceLimit;
    disableEmailNotificationsOnTranscriptionCompleted?: boolean;
    // Per-feature default instructions the server applies to first-time AI
    // content generation (explicit regenerate instructions always win).
    aiInstructions?: { summary?: string; quiz?: string; flashcard?: string };
  };
  allTags?: Tag[];
};

export type Tag = {
  name: string;
  color: string;
  createdAt: string;
  notesIds?: string[];
};

export type Transcript = {
  id: string;
  type?:
    | "voice_recording"
    | "audio_or_video_import"
    | "take_photo_or_import_image_or_pdf";
  createdAt: string;
  updatedAt?: string;
  fileName: string;
  language: string;
  audioUrl?: string;
  tagsIds?: string[];
  chatId?: string;
  isRetry?: boolean;
  falAIRequestId?: string;
  recordingFinalizedAt?: string;
  recordingRecoveredAt?: string;
  processingStartedAt?: string;
  status?:
    | "upload_started"
    | "upload_completed"
    | "upload_failed"
    | "transcription_started"
    | "transcription_completed"
    | "transcription_failed";
  statusDetail?: "retry_requested" | "retrying";
  originalFileUrl?: string;
  isFullTranscript?: boolean;
  transcriptionError?: string;
  // false: the segments are a free-model draft (server-owned).
  transcribedWithPremium?: boolean;
  // Set only on items from Recently deleted.
  deletedAt?: string;
  requestId?: string;
  summary?: string;
  translation?: { [translationLanguage: string]: Transcript["segments"] };
  flashcard?: { question: string; correctAnswer: string }[];
  quiz?: {
    questions: {
      question: string;
      options: string[];
      correctAnswer: string;
    }[];
  };
  withSpeakerLabels?: boolean;
  transcriptionProvider?:
    | "deepinfra/whisper-large-v3"
    | "deepinfra/whisper-large-v3-turbo"
    | "vertex/gemini-3.1-flash-lite"
    | "openrouter/google/gemini-3.1-flash-lite"
    | "vertex/gemini-3.1-flash-lite:batch"
    | "fal-ai/wizper"
    | "fal-ai/whisper"
    | "modulate"
    | "assemblyai";
  segments: {
    end: number;
    speaker?: string;
    start: number;
    text: string;
  }[];
};

export type AuthUser = {
  userId: string;
  email: string;
  exp?: number;
  iat?: number;
  nbf?: number;
  isAuthenticated?: boolean;
  ip?: string;
  country?: string;
};

export type SubscriptionPlan =
  | "low_tier_weekly"
  | "low_tier_monthly"
  | "low_tier_annual"
  | "mid_tier_weekly"
  | "mid_tier_monthly"
  | "mid_tier_annual"
  | "top_tier_weekly"
  | "top_tier_monthly"
  | "top_tier_annual";

export type RevenueCatPackageId = SubscriptionPlan;

export type PurchasePackage = {
  identifier: string;
  product: {
    price: number;
    priceString: string;
    pricePerWeek?: number;
    pricePerWeekString?: string;
  };
};

export type PurchaseCustomerInfo = {
  activeSubscriptions: string[];
};
